import { formatDateDisplay } from '@/utils/participantDetailDisplay';
import type { ProntuarioClinicalHistoryRecord } from '../types/prontuarioClinicalHistory';
import type { ProntuarioTherapeuticPlanRecord } from '../types/prontuarioTherapeuticPlan';

const EMPTY_VALUE = '—';

const formatIcd10Display = (
	primary: string | null | undefined,
	secondary: string | null | undefined,
): string | undefined => {
	const parts = [primary, secondary]
		.map((value) => value?.trim())
		.filter((value): value is string => Boolean(value));

	return parts.length > 0 ? parts.join(' · ') : undefined;
};

const formatSessionDurationDisplay = (
	minutes: number | null | undefined,
): string | undefined => {
	if (minutes == null || minutes < 1) return undefined;
	return `${minutes} min`;
};

const SYMPTOM_SEVERITY_LABELS: Record<
	MedicalRecords.SymptomSeverityLevel,
	string
> = {
	MINIMAL: 'Mínimo',
	MILD: 'Leve',
	MODERATE: 'Moderado',
	SEVERE: 'Grave',
};

const formatSymptomScoreDisplay = (
	score: number | null,
	level: MedicalRecords.SymptomSeverityLevel | null,
): string | undefined => {
	const levelLabel = level ? SYMPTOM_SEVERITY_LABELS[level] : null;

	if (score != null && levelLabel) return `${score} (${levelLabel})`;
	if (score != null) return String(score);
	return levelLabel ?? undefined;
};

export const resolveActivePlan = (
	plans: MedicalRecords.Plan[] | undefined,
): MedicalRecords.Plan | null => {
	if (!plans?.length) return null;
	return plans.find((plan) => plan.active) ?? plans[0] ?? null;
};

export const resolveLatestAssessment = (
	assessments: MedicalRecords.Assessment[] | undefined,
): MedicalRecords.Assessment | null => {
	const visible = (assessments ?? []).filter(
		(assessment) => !assessment.deleted_at,
	);
	if (!visible.length) return null;

	return [...visible].sort(
		(a, b) => b.assessment_number - a.assessment_number,
	)[0];
};

export const mapAssessmentToClinicalHistory = (
	assessment: MedicalRecords.Assessment | null | undefined,
): ProntuarioClinicalHistoryRecord | null => {
	if (!assessment) return null;

	const record: ProntuarioClinicalHistoryRecord = {
		instrumentosUtilizados:
			assessment.instruments_used?.trim() || undefined,
		impressaoClinica: assessment.clinical_impression?.trim() || undefined,
		cid10Hipoteses: formatIcd10Display(
			assessment.icd10_primary,
			assessment.icd10_secondary,
		),
		diagnosticoDiferencial:
			assessment.differential_diagnosis?.trim() || undefined,
		bai: formatSymptomScoreDisplay(
			assessment.bai_score,
			assessment.bai_level,
		),
		bdi: formatSymptomScoreDisplay(
			assessment.bdi_score,
			assessment.bdi_level,
		),
	};

	return Object.values(record).some(Boolean) ? record : null;
};

export const mapPlanToTherapeuticPlan = (
	plan: MedicalRecords.Plan | null | undefined,
): ProntuarioTherapeuticPlanRecord | null => {
	if (!plan) return null;

	const record: ProntuarioTherapeuticPlanRecord = {
		abordagemTeorica: plan.theoretical_approach?.trim() || undefined,
		frequencia: plan.frequency?.trim() || undefined,
		formato: plan.format?.trim() || undefined,
		duracaoSessoes: formatSessionDurationDisplay(plan.session_duration_min),
		duracaoEstimada: plan.estimated_duration?.trim() || undefined,
		objetivosTerapeuticos: plan.objectives?.trim() || undefined,
		tecnicasPrevistas: plan.planned_techniques?.trim() || undefined,
		encaminhamentos: plan.referrals?.trim() || undefined,
	};

	return Object.values(record).some(Boolean) ? record : null;
};

export type ProntuarioPatientContext = {
	firstSessionDate: string;
	frequency: string;
	clinicalHistory: ProntuarioClinicalHistoryRecord | null;
	therapeuticPlan: ProntuarioTherapeuticPlanRecord | null;
};

export const buildProntuarioPatientContext = (
	medicalRecord: MedicalRecords.MedicalRecord | null | undefined,
	patient?: Patients.Patient | null,
): ProntuarioPatientContext => {
	const activePlan = resolveActivePlan(medicalRecord?.plans);
	const latestAssessment = resolveLatestAssessment(
		medicalRecord?.assessments,
	);

	const firstSessionDate =
		formatDateDisplay(medicalRecord?.started_at) ??
		formatDateDisplay(medicalRecord?.opened_at) ??
		formatDateDisplay(patient?.opened_at) ??
		EMPTY_VALUE;

	const frequency = activePlan?.frequency?.trim() ?? '';

	return {
		firstSessionDate,
		frequency,
		clinicalHistory: mapAssessmentToClinicalHistory(latestAssessment),
		therapeuticPlan: mapPlanToTherapeuticPlan(activePlan),
	};
};
